import {
  type CodeRuleInput,
  can,
  canAnywhere,
  DEFAULT_MARKS,
  PSD_STAGES,
  parseSetCode,
  RECIPIENT_KINDS,
  REVISION_ACTIONS,
  type RecipientKind,
  type RevisionAction,
  type SetDetail,
  type SetSummary,
  sheetListProblems,
  TRANSMITTAL_DIRECTIONS,
  TRANSMITTAL_STATUSES,
  type TransmittalView,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { AlertTriangle, FileText, MessageCircleQuestion, Plus, Stamp } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, plural, todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { LinkedChanges } from '../changes/ChangesPage';
import { SetEstimateItems } from '../estimates/ItemLinks';
import { DistributionPanel, ReadBanner } from './Distribution';
import { RevisionImpactPanel } from './Impact';
import { ReviewRoutesPanel, RevisionReviewPanel } from './Review';
import { SaveOffline } from './SaveOffline';
import { PsdSearchBox, PsdSearchResults } from './Search';
import { TransmittalLinks } from './SharePage';
import { SplitDialog } from './Split';
import {
  ChunkedUploadButton,
  FileLink,
  ProjectFilter,
  PSD_ACCEPT,
  projectTarget,
  RevisionBadge,
  Tabs,
  useProjectDefault,
  useProjectsWith,
} from './shared';
import { FolderUpload, MoveSetDialog, NO_OBJECT, objectPath, PsdTreeView, psdTreeQuery } from './Tree';

type Tab = 'sets' | 'transmittals' | 'rule';

const setsQuery = (projectId?: string) => ({
  queryKey: ['psd-sets', projectId ?? 'all'],
  queryFn: () => api<SetSummary[]>('/psd/sets', { query: { projectId } }),
});

/* ───────────────────────── Реестр ПСД ───────────────────────── */

export function PsdPage() {
  const search = useSearch({ strict: false }) as {
    project?: string;
    tab?: Tab;
    q?: string;
    object?: string;
    mark?: string;
    view?: 'list';
  };
  const navigate = useNavigate();
  const me = useMe();
  const tab: Tab = search.tab ?? 'sets';
  const projectId = search.project;
  const setSearch = (
    patch: Partial<{
      project: string | undefined;
      tab: Tab;
      q: string | undefined;
      object: string | undefined;
      mark: string | undefined;
      view: 'list' | undefined;
    }>,
  ) => navigate({ to: '/psd', search: { ...search, ...patch } as never, replace: true });
  const uploaders = useProjectsWith('psd.upload');
  const approvers = useProjectsWith('psd.approve');
  const [creating, setCreating] = useState(false);
  const [transmittal, setTransmittal] = useState<'in' | 'out' | null>(null);
  // Папки ПСД — по проекту; «Все проекты» и view=list — прежний список альбомов
  const folders = tab === 'sets' && Boolean(projectId) && search.view !== 'list';
  const tree = useQuery({ ...psdTreeQuery(projectId ?? ''), enabled: folders });
  const path = tree.data ? objectPath(tree.data.objects, search.object) : [];
  useCrumbs(
    tab !== 'sets'
      ? [{ label: tab === 'transmittals' ? 'Накладные' : 'Правило и маршруты' }]
      : folders && tree.data && (search.object || search.mark)
        ? [
            { label: tree.data.projectCode, to: `/psd?project=${projectId}` },
            ...(search.object === NO_OBJECT
              ? [{ label: 'Без объекта', to: `/psd?project=${projectId}&object=${NO_OBJECT}` }]
              : path.map((o) => ({
                  label: o.code ?? o.name,
                  title: o.name,
                  to: `/psd?project=${projectId}&object=${o.id}`,
                }))),
            ...(search.mark ? [{ label: `Раздел ${search.mark}` }] : []),
          ]
        : null,
  );
  const staff = me.tenant?.staff ?? false;
  const tabs: Array<{ value: Tab; label: string }> = [{ value: 'sets', label: 'Комплекты' }];
  if (staff && uploaders.list.length) tabs.push({ value: 'transmittals', label: 'Накладные' });
  if (approvers.list.length) tabs.push({ value: 'rule', label: 'Правило и маршруты' });
  return (
    <Page>
      <PageHeader
        title="Проектная документация"
        subtitle="Комплекты по шифрам, листы и ревизии. На площадке видна только ревизия со штампом «В производство работ»."
        actions={
          tab === 'sets' && uploaders.list.length ? (
            <Button
              variant="primary"
              size="lg"
              icon={<Plus className="size-4" />}
              onClick={() => setCreating(true)}
            >
              Новый комплект
            </Button>
          ) : tab === 'transmittals' ? (
            <>
              <Button
                variant="primary"
                size="lg"
                icon={<Plus className="size-4" />}
                onClick={() => setTransmittal('in')}
              >
                Новая входящая накладная
              </Button>
              <Button size="lg" onClick={() => setTransmittal('out')}>
                Новая исходящая
              </Button>
            </>
          ) : null
        }
      />
      <Tabs
        value={tab}
        onChange={(v) => setSearch({ tab: v === 'sets' ? undefined : v } as never)}
        items={tabs}
      />
      <div className="mb-4">
        <ProjectFilter
          value={projectId}
          onChange={(v) => setSearch({ project: v })}
          perms={tab === 'rule' ? ['psd.approve'] : ['psd.view']}
          allLabel={tab === 'rule' ? 'Выберите проект' : 'Все проекты'}
        />
      </div>
      {tab === 'sets' ? (
        <PsdSearchBox value={search.q ?? ''} onChange={(v) => setSearch({ q: v.trim() ? v : undefined })} />
      ) : null}
      {tab === 'sets' && (search.q ?? '').trim().length >= 2 ? (
        <PsdSearchResults q={search.q!} projectId={projectId} />
      ) : null}
      {tab === 'sets' && (search.q ?? '').trim().length < 2 ? (
        folders ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {tree.data?.canUpload ? <FolderUpload projectId={projectId!} /> : null}
              <Button variant="ghost" className="h-11 md:h-10" onClick={() => setSearch({ view: 'list' })}>
                Списком
              </Button>
            </div>
            <PsdTreeView
              projectId={projectId!}
              objectId={search.object}
              mark={search.mark}
              onFilter={(f) => setSearch({ object: f.object, mark: f.mark })}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {projectId ? (
              <div>
                <Button
                  variant="ghost"
                  className="h-11 md:h-10"
                  onClick={() => setSearch({ view: undefined })}
                >
                  По папкам: объект → раздел → альбом
                </Button>
              </div>
            ) : null}
            <SetsTable projectId={projectId} />
          </div>
        )
      ) : null}
      {tab === 'transmittals' ? <TransmittalsTable projectId={projectId} /> : null}
      {tab === 'rule' ? (
        projectId ? (
          <div className="flex flex-col gap-4">
            <CodeRulePanel projectId={projectId} />
            <ReviewRoutesPanel projectId={projectId} />
          </div>
        ) : (
          <EmptyState title="Выберите проект">
            Правило шифра задаётся на проект и проверяет все его комплекты.
          </EmptyState>
        )
      ) : null}
      {creating ? <NewSetDialog projectId={projectId} onClose={() => setCreating(false)} /> : null}
      {transmittal ? (
        <NewTransmittalDialog
          direction={transmittal}
          projectId={projectId}
          onClose={() => setTransmittal(null)}
        />
      ) : null}
    </Page>
  );
}

function SetsTable({ projectId }: { projectId?: string }) {
  const q = useQuery(setsQuery(projectId));
  const [filter, setFilter] = useState('');
  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (q.data ?? []).filter(
      (s) =>
        !f ||
        s.code.toLowerCase().includes(f) ||
        s.name.toLowerCase().includes(f) ||
        (s.mark ?? '').toLowerCase() === f,
    );
  }, [q.data, filter]);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return (
      <EmptyState title="Комплектов пока нет">
        Комплект заводится по шифру вида 227-23-РП-ВВ-1-КЖ: портал разберёт его на стадию, объект и марку и
        сверит с объектами проекта.
      </EmptyState>
    );
  const quarantine = q.data.filter((s) => s.state === 'quarantine').length;
  const overdue = q.data.filter((s) => s.overdue).length;
  return (
    <div className="flex flex-col gap-4">
      <TitleBlock
        items={[
          { label: 'Комплектов', value: q.data.length },
          {
            label: 'В производстве работ',
            value: q.data.filter((s) => s.activeRevision).length,
            note: 'со штампом',
          },
          {
            label: 'В карантине',
            value: quarantine,
            tone: quarantine ? 'danger' : 'default',
            note: 'шифр не проходит правило',
          },
          { label: 'Просрочено проектировщиком', value: overdue, tone: overdue ? 'danger' : 'default' },
        ]}
      />
      <Input
        aria-label="Поиск по шифру и названию"
        placeholder="Поиск: шифр, название или марка"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="h-11 md:h-10 md:max-w-md"
      />
      {/* Телефон: карточки */}
      <ul className="flex flex-col gap-2 md:hidden">
        {rows.map((s) => (
          <li key={s.id}>
            <Link
              to="/psd/sets/$setId"
              params={{ setId: s.id }}
              className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-4"
            >
              <span className="font-medium [overflow-wrap:anywhere] text-ink">{s.code}</span>
              <span className="text-[14px] text-ink-2">{s.name}</span>
              <span className="flex flex-wrap gap-1.5">
                {s.activeRevision ? (
                  <Badge tone="ink">{s.activeRevision} в производстве</Badge>
                ) : (
                  <Badge>нет действующей</Badge>
                )}
                {s.state === 'quarantine' ? <Badge tone="danger">карантин</Badge> : null}
                {s.openQuestions ? <Badge tone="warning">вопросов {s.openQuestions}</Badge> : null}
                {s.openRemarks ? <Badge tone="warning">замечаний {s.openRemarks}</Badge> : null}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <Panel bodyClassName="p-0" className="hidden md:block">
        <ScrollX>
          <table className="w-full min-w-[980px] text-[14px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2.5 pr-3 pl-5 font-medium">Шифр</th>
                <th className="py-2.5 pr-3 font-medium">Стадия, марка, объект</th>
                <th className="py-2.5 pr-3 font-medium">Действующая</th>
                <th className="py-2.5 pr-3 font-medium">Последняя ревизия</th>
                <th className="py-2.5 pr-3 text-right font-medium">Листов</th>
                <th className="py-2.5 pr-3 font-medium">Вопросы и замечания</th>
                <th className="py-2.5 pr-5 font-medium">Выдача</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-t border-line align-top">
                  <td className="py-2.5 pr-3 pl-5">
                    <Link
                      to="/psd/sets/$setId"
                      params={{ setId: s.id }}
                      className="font-medium text-ink hover:underline"
                    >
                      {s.code}
                    </Link>
                    <div className="text-[13px] text-ink-2">{s.name}</div>
                    {s.state === 'quarantine' ? (
                      <div className="mt-1">
                        <Badge tone="danger">карантин загрузки</Badge>
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2.5 pr-3 text-ink-2">
                    {[s.stage, s.mark, s.object].filter(Boolean).join(' · ') || '—'}
                    <div className="text-[12px]">{s.projectCode}</div>
                  </td>
                  <td className="py-2.5 pr-3">
                    {s.activeRevision ? (
                      <Badge tone="ink">{s.activeRevision}</Badge>
                    ) : (
                      <span className="text-ink-3">нет</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3">
                    {s.lastRevision && s.lastRevision !== s.activeRevision ? (
                      <span className="flex items-center gap-1.5">
                        <span className="num">{s.lastRevision}</span> <RevisionBadge status={s.lastStatus} />
                      </span>
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                  <td className="num py-2.5 pr-3 text-right">{s.sheetCount}</td>
                  <td className="py-2.5 pr-3">
                    {s.openQuestions || s.openRemarks ? (
                      <span className="flex flex-wrap gap-1">
                        {s.openQuestions ? <Badge tone="warning">вопросов {s.openQuestions}</Badge> : null}
                        {s.openRemarks ? <Badge tone="warning">замечаний {s.openRemarks}</Badge> : null}
                      </span>
                    ) : (
                      <span className="text-ink-3">нет открытых</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-5 text-[13px]">
                    {s.receivedDate ? (
                      <span className="text-ink-2">получен {date(s.receivedDate)}</span>
                    ) : s.plannedDate ? (
                      <span className={s.overdue ? 'text-danger' : 'text-ink-2'}>
                        план {date(s.plannedDate)}
                        {s.overdue ? ', просрочен' : ''}
                      </span>
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      </Panel>
    </div>
  );
}

/** Ведомость листов из вставки: «1 Общие данные» или «1<Tab>Общие данные» по строкам */
function parseSheets(text: string): Array<{ number: string; name: string }> {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const m = /^([0-9A-Za-zА-Яа-яЁё.\-/]+)[\s\t;.,:-]+(.+)$/.exec(l);
      return m ? { number: m[1]!, name: m[2]!.trim() } : { number: l, name: `Лист ${l}` };
    });
}

function NewSetDialog({ projectId, onClose }: { projectId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { list } = useProjectsWith('psd.upload');
  const designers = useQuery(counterpartiesQuery('designer'));
  const [f, setF] = useState({
    projectId: projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? ''),
    code: '',
    name: '',
    designerId: '',
    plannedDate: '',
    sheets: '',
  });
  const setProjectDefaultNewSetDialog = useCallback(
    (id: string) => setF((x) => ({ ...x, projectId: id })),
    [],
  );
  useProjectDefault(list, projectId, f.projectId, setProjectDefaultNewSetDialog);
  const project = list.find((p) => p.id === f.projectId);
  const rule = useQuery({
    queryKey: ['psd-rule', f.projectId],
    queryFn: () => api<CodeRuleInput & { custom: boolean }>(`/psd/projects/${f.projectId}/code-rule`),
    enabled: Boolean(f.projectId),
  });
  const parsed =
    rule.data && f.code.trim()
      ? parseSetCode(
          f.code,
          rule.data,
          (project?.objects ?? []).map((o) => o.code).filter((c): c is string => Boolean(c)),
        )
      : null;
  const sheets = parseSheets(f.sheets);
  const sheetProblems = sheetListProblems(sheets.map((s) => s.number));
  const m = useMutation({
    mutationFn: () =>
      api<SetDetail>('/psd/sets', {
        body: {
          projectId: f.projectId,
          code: f.code.trim(),
          name: f.name.trim(),
          designerId: f.designerId || null,
          plannedDate: f.plannedDate || null,
          sheets: sheets.length ? sheets : undefined,
        },
      }),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['psd-sets'] });
      toast(
        s.state === 'quarantine'
          ? `Комплект ${s.code} в карантине: исправьте шифр`
          : `Комплект ${s.code} заведён, ревизия R00`,
      );
      onClose();
      navigate({ to: '/psd/sets/$setId', params: { setId: s.id } });
    },
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      width="lg"
      title="Новый комплект"
      description="Шифр проверяется по правилу проекта. Если не проходит — комплект попадёт в карантин и не выпустится, пока шифр не исправят."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!f.projectId || f.code.trim().length < 3 || f.name.trim().length < 2}
            onClick={() => m.mutate()}
          >
            Завести комплект
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект" className="sm:col-span-2">
          <Select
            className="h-11 md:h-10"
            value={f.projectId}
            onChange={(e) => set('projectId', e.target.value)}
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Шифр"
          className="sm:col-span-2"
          hint={
            rule.data
              ? `Правило: ${rule.data.prefix ? `${rule.data.prefix}${rule.data.separator}` : ''}стадия${rule.data.separator}объект${rule.data.separator}марка`
              : undefined
          }
        >
          <Input
            className="h-11 md:h-10"
            value={f.code}
            onChange={(e) => set('code', e.target.value)}
            placeholder={`${rule.data?.prefix || '227-23'}-РП-ВВ-1-КЖ`}
          />
        </Field>
        {parsed ? (
          <div className="sm:col-span-2">
            {parsed.problems.length ? (
              <div className="rounded-md bg-danger-soft px-3 py-2.5 text-[13px] text-danger" role="alert">
                <div className="font-medium">Шифр не проходит правило — комплект уйдёт в карантин:</div>
                <ul className="mt-1 list-disc pl-5">
                  {parsed.problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="rounded-md bg-success-soft px-3 py-2.5 text-[13px] text-success">
                Стадия {parsed.stage} · объект {parsed.object} · марка {parsed.mark}
                {parsed.markNo ? ` ${parsed.markNo}` : ''}
              </div>
            )}
          </div>
        ) : null}
        <Field label="Наименование" className="sm:col-span-2">
          <Input className="h-11 md:h-10" value={f.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Проектировщик">
          <Select
            className="h-11 md:h-10"
            value={f.designerId}
            onChange={(e) => set('designerId', e.target.value)}
          >
            <option value="">— не указан —</option>
            {(designers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.shortName || c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="План выдачи по договору" hint="Просрочка видна в реестре">
          <Input
            className="h-11 md:h-10"
            type="date"
            value={f.plannedDate}
            onChange={(e) => set('plannedDate', e.target.value)}
          />
        </Field>
        <Field
          label="Ведомость листов"
          className="sm:col-span-2"
          hint="По строке на лист: номер и наименование. Можно вставить из Excel."
          error={sheetProblems.length ? sheetProblems.join('; ') : undefined}
        >
          <Textarea
            rows={5}
            value={f.sheets}
            onChange={(e) => set('sheets', e.target.value)}
            placeholder={'1\tОбщие данные\n2\tПлан трассы\n3\tПродольный профиль'}
          />
        </Field>
        {sheets.length ? (
          <p className="text-[13px] text-ink-2 sm:col-span-2">
            {plural(sheets.length, 'лист', 'листа', 'листов')}
          </p>
        ) : null}
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

/* ───────────────────────── Карточка комплекта ───────────────────────── */

export function SetPage() {
  const { setId } = useParams({ strict: false }) as { setId: string };
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['psd-set', setId], queryFn: () => api<SetDetail>(`/psd/sets/${setId}`) });
  const project = useQuery({ ...projectQuery(q.data?.projectId ?? ''), enabled: Boolean(q.data?.projectId) });
  // Ревизия из адреса (?rev=) — из поиска по ПСД и ссылок на конкретную ревизию
  const { rev: revParam } = useSearch({ strict: false }) as { rev?: string };
  const [revId, setRevId] = useState<string | null>(revParam ?? null);
  const [editSheets, setEditSheets] = useState(false);
  const [editCode, setEditCode] = useState(false);
  const [moving, setMoving] = useState(false);
  const [act, setAct] = useState<RevisionAction | null>(null);
  const [split, setSplit] = useState<{ id: string; name: string } | null>(null);
  useCrumbs(
    q.data
      ? [{ label: q.data.projectCode, to: `/psd?project=${q.data.projectId}` }, { label: q.data.code }]
      : null,
    q.error,
  );
  const refresh = (d?: unknown) => {
    if (d && typeof d === 'object' && 'revisions' in d) qc.setQueryData(['psd-set', setId], d);
    else qc.invalidateQueries({ queryKey: ['psd-set', setId] });
    qc.invalidateQueries({ queryKey: ['psd-sets'] });
  };
  const newRev = useMutation({
    mutationFn: () => api<SetDetail>(`/psd/sets/${setId}/revisions`, { body: {} }),
    onSuccess: (d) => {
      refresh(d);
      setRevId(d.revisions[0]?.id ?? null);
      toast(`Создана ревизия ${d.revisions[0]?.code}: листы перенесены, замените изменённые`);
    },
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const s = q.data;
  const target = project.data ? projectTarget(project.data) : { projectId: s.projectId };
  const canUpload = can(me.access, 'psd.upload', target);
  const canApprove = can(me.access, 'psd.approve', target);
  const canStamp = can(me.access, 'psd.stamp', target);
  const rev = s.revisions.find((r) => r.id === revId) ?? s.revisions[0];
  const openRev = s.revisions.find((r) =>
    ['development', 'review', 'remarks', 'approved'].includes(r.status),
  );
  const editable = rev && ['development', 'remarks'].includes(rev.status) && canUpload;
  const actions: RevisionAction[] = [];
  if (rev) {
    if (canUpload && editable) actions.push('submit');
    if (canApprove && rev.status === 'review') actions.push('approve', 'remarks');
    if (canStamp && rev.status === 'approved') actions.push('stamp');
    if (canApprove && rev.status !== 'annulled') actions.push('annul');
  }
  const activeRev = s.revisions.find((r) => r.status === 'production');
  const noFile = rev ? rev.sheets.filter((x) => !x.fileId).length : 0;
  return (
    <Page>
      <PageHeader
        title={<span className="[overflow-wrap:anywhere]">{s.code}</span>}
        subtitle={s.name}
        actions={
          canUpload && !openRev && !s.activeOnly ? (
            <Button
              variant="primary"
              size="lg"
              icon={<Plus className="size-4" />}
              loading={newRev.isPending}
              onClick={() => newRev.mutate()}
            >
              Новая ревизия
            </Button>
          ) : null
        }
      >
        <div className="mt-2 flex flex-wrap gap-1.5">
          {s.activeRevision ? (
            <Badge tone="ink">
              <Stamp className="size-3.5" /> {s.activeRevision} в производство работ
            </Badge>
          ) : (
            <Badge>нет действующей ревизии</Badge>
          )}
          {s.state === 'quarantine' ? <Badge tone="danger">карантин загрузки</Badge> : null}
          {s.overdue ? <Badge tone="danger">просрочен проектировщиком</Badge> : null}
        </div>
      </PageHeader>
      <ErrorBox error={newRev.error} className="mb-4" />
      {s.state === 'quarantine' ? (
        <div
          className="mb-4 flex flex-col gap-2 rounded-lg border border-danger/40 bg-danger-soft p-4 text-[14px] text-danger"
          role="alert"
        >
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="size-4" /> Шифр не проходит правило проекта — комплект не публикуется
          </div>
          <ul className="list-disc pl-5">
            {s.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          {canUpload ? (
            <div>
              <Button className="h-11 md:h-10" onClick={() => setEditCode(true)}>
                Исправить шифр
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      <ReadBanner setId={s.id} />
      <div className="mb-4">
        <SaveOffline
          label={`Комплект ${s.code}`}
          activeRevisionId={activeRev?.id ?? null}
          sheets={(activeRev?.sheets ?? []).flatMap((x) =>
            x.fileId
              ? [
                  {
                    sheetId: x.sheetId,
                    number: x.number,
                    setId: s.id,
                    setCode: s.code,
                    revisionId: activeRev!.id,
                    revision: activeRev!.code,
                    fileId: x.fileId,
                    fileName: x.fileName,
                  },
                ]
              : [],
          )}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="flex min-w-0 flex-col gap-4">
          {s.revisions.length > 1 ? (
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Ревизии">
              {s.revisions.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={r.id === rev?.id}
                  onClick={() => setRevId(r.id)}
                  className={clsx(
                    'num h-11 rounded-md border px-3 text-[14px] md:h-9',
                    r.id === rev?.id
                      ? 'border-ink bg-ink text-on-ink'
                      : 'border-line-strong bg-surface text-ink',
                  )}
                >
                  {r.code}
                </button>
              ))}
            </div>
          ) : null}
          {rev ? (
            <Panel
              title={`Ревизия ${rev.code}`}
              meta={<RevisionBadge status={rev.status} />}
              actions={actions.map((a) => (
                <Button
                  key={a}
                  variant={
                    a === 'stamp' || (a === 'submit' && actions.length === 2) || a === 'approve'
                      ? 'primary'
                      : a === 'annul'
                        ? 'danger'
                        : 'secondary'
                  }
                  className="h-11 md:h-10"
                  onClick={() => setAct(a)}
                >
                  {REVISION_ACTIONS[a]}
                </Button>
              ))}
              bodyClassName="p-0"
            >
              {rev.stampDate ? (
                <p className="border-b border-line px-5 py-3 text-[13px] text-ink-2">
                  Штамп «В производство работ» {date(rev.stampDate)}
                  {rev.stampedBy ? `, ${rev.stampedBy}` : ''}. Подпись ЭЦП ГИП — после подключения NCALayer.
                </p>
              ) : null}
              {editable && noFile ? (
                <p className="border-b border-line bg-warning-soft px-5 py-3 text-[13px] text-warning">
                  Без файла {plural(noFile, 'лист', 'листа', 'листов')} — штамп на такую ревизию не ставится.
                  Загрузите PDF: файл с номером листа в имени («…_л3.pdf») встанет на лист сам.
                </p>
              ) : null}
              <ul className="divide-y divide-line">
                {rev.sheets.map((sh) => (
                  <li
                    key={sh.sheetId}
                    className="flex flex-col gap-1 px-5 py-3 md:flex-row md:items-center md:gap-4"
                  >
                    <Link
                      to="/psd/sheets/$sheetId"
                      params={{ sheetId: sh.sheetId }}
                      className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-ink hover:underline md:min-h-0"
                    >
                      <FileText className="size-4 shrink-0 text-ink-2" />
                      <span className="num shrink-0 font-medium">Лист {sh.number}</span>
                      <span className="truncate text-ink-2">{sh.name}</span>
                    </Link>
                    <span className="flex flex-wrap items-center gap-1.5 text-[13px]">
                      {sh.fileId ? (
                        <FileLink id={sh.fileId} name={sh.fileName ?? 'файл'} />
                      ) : (
                        <span className="text-warning">нет файла</span>
                      )}
                      {sh.changed && rev.index > 0 ? <Badge tone="accent">изменён</Badge> : null}
                      {sh.openQuestions ? <Badge tone="warning">вопросов {sh.openQuestions}</Badge> : null}
                      {sh.openRemarks ? <Badge tone="warning">замечаний {sh.openRemarks}</Badge> : null}
                    </span>
                    {editable ? (
                      <ChunkedUploadButton
                        revisionId={rev.id}
                        sheetId={sh.sheetId}
                        accept=".pdf"
                        multiple={false}
                        label="Заменить"
                        onDone={refresh}
                      />
                    ) : null}
                  </li>
                ))}
                {!rev.sheets.length ? (
                  <li className="px-5 py-6 text-[14px] text-ink-2">В ведомости нет листов.</li>
                ) : null}
              </ul>
            </Panel>
          ) : (
            <EmptyState title={s.activeOnly ? 'Действующей ревизии нет' : 'Ревизий нет'}>
              {s.activeOnly
                ? 'Комплект ещё не выпущен в производство работ. Как только ГИП поставит штамп, листы появятся здесь.'
                : 'Создайте ревизию, чтобы загрузить листы.'}
            </EmptyState>
          )}
          {rev && (!s.activeOnly || rev.status === 'review') ? (
            <RevisionReviewPanel revisionId={rev.id} setId={s.id} />
          ) : null}
          {rev && (!s.activeOnly || rev.status === 'production') ? (
            <RevisionImpactPanel revisionId={rev.id} projectId={s.projectId} />
          ) : null}
          {rev && !s.activeOnly ? (
            <Panel
              title="Файлы ревизии"
              meta="PDF, DWG, DXF, IFC, XLSX, ZIP, RAR до 2 ГБ, с докачкой"
              actions={
                editable ? (
                  <ChunkedUploadButton
                    revisionId={rev.id}
                    accept={PSD_ACCEPT}
                    label="Загрузить файлы"
                    onDone={refresh}
                  />
                ) : null
              }
            >
              {rev.files.length ? (
                <ul className="flex flex-col gap-1 text-[14px]">
                  {rev.files.map((f) => (
                    <li key={f.id} className="flex flex-wrap items-center gap-2">
                      <FileLink id={f.id} name={f.name} />
                      <span className="text-[12px] text-ink-2">
                        {(f.sizeBytes / 1024 / 1024).toFixed(1)} МБ
                      </span>
                      {/\.(dxf|ifc|dwg)$/i.test(f.name) ? (
                        <Link
                          to="/psd/files/$fileId"
                          params={{ fileId: f.id }}
                          className="inline-flex min-h-11 items-center text-[13px] font-medium text-ink underline-offset-4 hover:underline md:min-h-0"
                        >
                          Открыть в просмотре
                        </Link>
                      ) : null}
                      {editable && /\.pdf$/i.test(f.name) ? (
                        <button
                          type="button"
                          onClick={() => setSplit({ id: f.id, name: f.name })}
                          className="inline-flex min-h-11 items-center text-[13px] font-medium text-ink underline-offset-4 hover:underline md:min-h-0"
                        >
                          Разложить по листам
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[14px] text-ink-2">Файлов нет.</p>
              )}
            </Panel>
          ) : null}
          {rev && !s.activeOnly && rev.events.length ? (
            <Panel title="Ход ревизии">
              <ol className="flex flex-col gap-2 text-[14px]">
                {rev.events.map((e, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: события идут по времени и не меняются
                  <li key={i}>
                    <span className="text-ink-2">{dateTime(e.at)}</span> · {e.action}
                    {e.by ? <span className="text-ink-2"> — {e.by}</span> : null}
                    {e.comment ? (
                      <div className="mt-0.5 whitespace-pre-wrap text-ink-2">{e.comment}</div>
                    ) : null}
                  </li>
                ))}
              </ol>
            </Panel>
          ) : null}
        </div>
        <div className="flex flex-col gap-4">
          <Panel
            title="Комплект"
            actions={
              canUpload && !s.activeOnly ? (
                <Button variant="ghost" className="h-11 md:h-9" onClick={() => setEditCode(true)}>
                  Изменить
                </Button>
              ) : null
            }
          >
            <KeyValue
              items={[
                {
                  label: 'Проект',
                  value: (
                    <Link
                      to="/psd"
                      search={{ project: s.projectId } as never}
                      className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0"
                    >
                      {s.projectCode}
                    </Link>
                  ),
                },
                {
                  label: 'Стадия',
                  value: s.stage
                    ? `${s.stage} — ${PSD_STAGES[s.stage as keyof typeof PSD_STAGES] ?? ''}`
                    : '—',
                },
                { label: 'Марка', value: s.mark },
                {
                  label: 'Объект',
                  value: (
                    <span className="flex flex-wrap items-center gap-x-3">
                      {s.objectId ? (
                        <Link
                          to="/psd"
                          search={{ project: s.projectId, object: s.objectId } as never}
                          className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0"
                        >
                          {s.object ?? 'объект'}
                        </Link>
                      ) : (
                        <span>{s.object ?? 'Без объекта'}</span>
                      )}
                      {canUpload && !s.activeOnly ? (
                        <button
                          type="button"
                          className="inline-flex min-h-11 items-center text-ink underline underline-offset-4 md:min-h-0"
                          onClick={() => setMoving(true)}
                        >
                          Перенести
                        </button>
                      ) : null}
                    </span>
                  ),
                },
                { label: 'Проектировщик', value: s.designerName },
                { label: 'План выдачи', value: s.plannedDate ? date(s.plannedDate) : null },
                { label: 'Получен', value: s.receivedDate ? date(s.receivedDate) : null },
              ]}
            />
          </Panel>
          <Panel
            title="Ведомость листов"
            meta={plural(s.sheets.length, 'лист', 'листа', 'листов')}
            actions={
              canUpload && !s.activeOnly ? (
                <Button variant="ghost" className="h-11 md:h-9" onClick={() => setEditSheets(true)}>
                  Изменить
                </Button>
              ) : null
            }
          >
            {s.sheetProblems.length ? (
              <ul className="mb-2 list-disc pl-5 text-[13px] text-danger">
                {s.sheetProblems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ) : null}
            <p className="text-[13px] text-ink-2">
              {s.openQuestions || s.openRemarks
                ? `Открыто: вопросов ${s.openQuestions}, замечаний ${s.openRemarks}`
                : 'Открытых вопросов и замечаний нет'}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Link
                to="/supervision"
                search={{ project: s.projectId, set: s.id, tab: 'questions' } as never}
                className="inline-flex min-h-11 items-center gap-1.5 text-[14px] text-ink underline-offset-4 hover:underline md:min-h-0"
              >
                <MessageCircleQuestion className="size-4" /> Вопросы по комплекту
              </Link>
              <Link
                to="/supervision"
                search={{ project: s.projectId, set: s.id } as never}
                className="inline-flex min-h-11 items-center gap-1.5 text-[14px] text-ink underline-offset-4 hover:underline md:min-h-0"
              >
                Замечания по комплекту
              </Link>
            </div>
          </Panel>
          <DistributionPanel setId={s.id} projectId={s.projectId} />
          <SetEstimateItems setId={s.id} />
          {!s.activeOnly ? <LinkedChanges type="set" id={s.id} /> : null}
          {s.transmittals.length ? (
            <Panel title="Накладные">
              <ul className="flex flex-col gap-1 text-[14px]">
                {s.transmittals.map((t) => (
                  <li key={t.id}>
                    <Link
                      to="/psd/transmittals/$transmittalId"
                      params={{ transmittalId: t.id }}
                      className="inline-flex min-h-11 items-center hover:underline md:min-h-0"
                    >
                      {t.number}
                    </Link>{' '}
                    <span className="text-ink-2">
                      {date(t.date)}
                      {t.party ? `, ${t.party}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}
        </div>
      </div>
      {split && rev ? (
        <SplitDialog
          revisionId={rev.id}
          fileId={split.id}
          fileName={split.name}
          onClose={() => setSplit(null)}
          onDone={refresh}
        />
      ) : null}
      {act && rev ? (
        <RevisionActionDialog
          action={act}
          revisionId={rev.id}
          code={rev.code}
          onClose={() => setAct(null)}
          onDone={refresh}
        />
      ) : null}
      {editSheets ? <SheetsDialog set={s} onClose={() => setEditSheets(false)} onDone={refresh} /> : null}
      {editCode ? <EditSetDialog set={s} onClose={() => setEditCode(false)} onDone={refresh} /> : null}
      {moving ? <MoveSetDialog set={s} onClose={() => setMoving(false)} onDone={refresh} /> : null}
    </Page>
  );
}

function RevisionActionDialog({
  action,
  revisionId,
  code,
  onClose,
  onDone,
}: {
  action: RevisionAction;
  revisionId: string;
  code: string;
  onClose: () => void;
  onDone: (d: unknown) => void;
}) {
  const [comment, setComment] = useState('');
  const [day, setDay] = useState(todayLocal());
  const need = action === 'remarks' || action === 'annul';
  const m = useMutation({
    mutationFn: () =>
      api<SetDetail>(`/psd/revisions/${revisionId}/action`, {
        body: { action, comment: comment.trim() || null, ...(action === 'stamp' ? { date: day } : {}) },
      }),
    onSuccess: (d) => {
      onDone(d);
      toast(
        action === 'stamp'
          ? `${code} — в производство работ. Прежняя ревизия погашена`
          : `${code}: ${REVISION_ACTIONS[action].toLowerCase()} — готово`,
      );
      onClose();
    },
  });
  const hints: Record<RevisionAction, string> = {
    submit: 'Ревизия уйдёт на проверку. Файлы в ней больше не меняются.',
    remarks: 'Ревизия вернётся автору. Опишите, что исправить.',
    approve: 'После утверждения ГИП ставит штамп «В производство работ».',
    stamp:
      'Ревизия станет действующей: площадка и подрядчики увидят только её, прежняя погасится. Открытые вопросы и замечания по изменённым листам перейдут на неё с отметкой «проверить актуальность».',
    annul: 'Ревизия будет аннулирована. Укажите причину.',
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`${REVISION_ACTIONS[action]}: ${code}`}
      description={hints[action]}
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant={action === 'annul' ? 'danger' : 'primary'}
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={need && !comment.trim()}
            onClick={() => m.mutate()}
          >
            {REVISION_ACTIONS[action]}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {action === 'stamp' ? (
          <Field label="Дата штампа">
            <Input
              type="date"
              className="h-11 md:h-10"
              value={day}
              onChange={(e) => setDay(e.target.value)}
            />
          </Field>
        ) : null}
        <Field label={need ? 'Комментарий (обязательно)' : 'Комментарий'}>
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

function SheetsDialog({
  set: s,
  onClose,
  onDone,
}: {
  set: SetDetail;
  onClose: () => void;
  onDone: (d: unknown) => void;
}) {
  const [text, setText] = useState(s.sheets.map((x) => `${x.number}\t${x.name}`).join('\n'));
  const sheets = parseSheets(text);
  const problems = sheetListProblems(sheets.map((x) => x.number));
  const m = useMutation({
    mutationFn: () => api<SetDetail>(`/psd/sets/${s.id}/sheets`, { method: 'PUT', body: { sheets } }),
    onSuccess: (d) => {
      onDone(d);
      toast('Ведомость листов сохранена');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Ведомость листов"
      description="По строке на лист: номер и наименование. Выпущенный лист или лист с вопросами убрать нельзя."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!sheets.length}
            onClick={() => m.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <Field
        label={plural(sheets.length, 'лист', 'листа', 'листов')}
        error={problems.length ? problems.join('; ') : undefined}
      >
        <Textarea
          rows={12}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="font-mono text-[13px]"
        />
      </Field>
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}

function EditSetDialog({
  set: s,
  onClose,
  onDone,
}: {
  set: SetDetail;
  onClose: () => void;
  onDone: (d: unknown) => void;
}) {
  const designers = useQuery(counterpartiesQuery('designer'));
  const [f, setF] = useState({
    code: s.code,
    name: s.name,
    designerId: s.designerId ?? '',
    plannedDate: s.plannedDate ?? '',
  });
  const m = useMutation({
    mutationFn: () =>
      api<SetDetail>(`/psd/sets/${s.id}`, {
        method: 'PATCH',
        body: {
          code: f.code.trim(),
          name: f.name.trim(),
          designerId: f.designerId || null,
          plannedDate: f.plannedDate || null,
        },
      }),
    onSuccess: (d) => {
      onDone(d);
      toast(d.state === 'quarantine' ? 'Шифр всё ещё не проходит правило' : 'Комплект сохранён');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Комплект"
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" className="h-11 md:h-10" loading={m.isPending} onClick={() => m.mutate()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field label="Шифр">
          <Input
            className="h-11 md:h-10"
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value })}
          />
        </Field>
        <Field label="Наименование">
          <Input
            className="h-11 md:h-10"
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Field>
        <Field label="Проектировщик">
          <Select
            className="h-11 md:h-10"
            value={f.designerId}
            onChange={(e) => setF({ ...f, designerId: e.target.value })}
          >
            <option value="">— не указан —</option>
            {(designers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.shortName || c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="План выдачи по договору">
          <Input
            type="date"
            className="h-11 md:h-10"
            value={f.plannedDate}
            onChange={(e) => setF({ ...f, plannedDate: e.target.value })}
          />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Правило шифра ───────────────────────── */

function CodeRulePanel({ projectId }: { projectId: string }) {
  const q = useQuery({
    queryKey: ['psd-rule', projectId],
    queryFn: () => api<CodeRuleInput & { custom: boolean }>(`/psd/projects/${projectId}/code-rule`),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return <CodeRuleForm key={projectId} projectId={projectId} rule={q.data} />;
}

function CodeRuleForm({ projectId, rule }: { projectId: string; rule: CodeRuleInput & { custom: boolean } }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    prefix: rule.prefix,
    separator: rule.separator,
    stages: rule.stages.join(', '),
    marks: rule.marks.join(', '),
    checkObject: rule.checkObject,
  });
  const list = (s: string) => [
    ...new Set(
      s
        .split(/[,;\s]+/)
        .map((x) => x.trim())
        .filter(Boolean),
    ),
  ];
  const m = useMutation({
    mutationFn: () =>
      api<{ rechecked: number; quarantine: number }>(`/psd/projects/${projectId}/code-rule`, {
        method: 'PUT',
        body: {
          prefix: f.prefix.trim(),
          separator: f.separator || '-',
          stages: list(f.stages),
          marks: list(f.marks),
          checkObject: f.checkObject,
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['psd-rule', projectId] });
      qc.invalidateQueries({ queryKey: ['psd-sets'] });
      toast(`Правило сохранено. Перепроверено комплектов: ${r.rechecked}, в карантине: ${r.quarantine}`);
    },
  });
  return (
    <Panel
      title="Правило шифра комплекта"
      meta={rule.custom ? 'задано для проекта' : 'по умолчанию'}
      actions={
        <Button variant="primary" className="h-11 md:h-10" loading={m.isPending} onClick={() => m.mutate()}>
          Сохранить и перепроверить
        </Button>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Начало шифра" hint="Номер договора проекта, например 227-23">
          <Input
            className="h-11 md:h-10"
            value={f.prefix}
            onChange={(e) => setF({ ...f, prefix: e.target.value })}
          />
        </Field>
        <Field label="Разделитель">
          <Input
            className="h-11 md:h-10"
            value={f.separator}
            maxLength={3}
            onChange={(e) => setF({ ...f, separator: e.target.value })}
          />
        </Field>
        <Field label="Стадии" hint="Через запятую">
          <Input
            className="h-11 md:h-10"
            value={f.stages}
            onChange={(e) => setF({ ...f, stages: e.target.value })}
          />
        </Field>
        <Field
          label="Марки комплектов"
          hint={`Через запятую. По умолчанию: ${DEFAULT_MARKS.slice(0, 8).join(', ')}…`}
        >
          <Textarea rows={3} value={f.marks} onChange={(e) => setF({ ...f, marks: e.target.value })} />
        </Field>
        <Checkbox
          className="md:col-span-2"
          checked={f.checkObject}
          onChange={(e) => setF({ ...f, checkObject: e.target.checked })}
          label="Объект в шифре должен совпадать с кодом объекта проекта"
          description="Например, «ОХЗ-1» вместо «ОЗХ-1» — комплект уйдёт в карантин"
        />
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Panel>
  );
}

/* ───────────────────────── Накладные ───────────────────────── */

function TransmittalsTable({ projectId }: { projectId?: string }) {
  const q = useQuery({
    queryKey: ['psd-transmittals', projectId ?? 'all'],
    queryFn: () => api<TransmittalView[]>('/psd/transmittals', { query: { projectId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return (
      <EmptyState title="Накладных пока нет">
        Входящая накладная фиксирует, какие комплекты и сколько экземпляров передал проектировщик; исходящая —
        какие ревизии и кому отправлены.
      </EmptyState>
    );
  return (
    <ul className="flex flex-col gap-2">
      {q.data.map((t) => (
        <li key={t.id}>
          <Link
            to="/psd/transmittals/$transmittalId"
            params={{ transmittalId: t.id }}
            className="flex flex-col gap-1 rounded-lg border border-line bg-surface p-4 hover:border-ink-3 md:flex-row md:items-center md:gap-4"
          >
            <span className="num font-medium text-ink md:w-36">{t.number}</span>
            <span className="text-[14px] text-ink-2 md:w-28">{date(t.date)}</span>
            <span className="min-w-0 flex-1 text-[14px] text-ink">
              {t.party ?? (t.recipientKind ? RECIPIENT_KINDS[t.recipientKind as RecipientKind] : '—')}
              <span className="text-ink-2">
                {' '}
                · {t.items.map((i) => `${i.setCode} ${i.revision}`).join(', ')}
              </span>
            </span>
            <Badge tone={t.status === 'draft' ? 'neutral' : t.status === 'sent' ? 'info' : 'success'}>
              {TRANSMITTAL_STATUSES[t.status]}
            </Badge>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function NewTransmittalDialog({
  direction,
  projectId,
  onClose,
}: {
  direction: 'in' | 'out';
  projectId?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { list } = useProjectsWith('psd.upload');
  const cps = useQuery(counterpartiesQuery(direction === 'in' ? 'designer' : undefined));
  const [pid, setPid] = useState(
    projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? ''),
  );
  const setProjectDefaultNewTransmittalDialog = useCallback(setPid, []);
  useProjectDefault(list, projectId, pid, setProjectDefaultNewTransmittalDialog);
  const sets = useQuery({ ...setsQuery(pid), enabled: Boolean(pid) });
  const details = useQuery({
    queryKey: ['psd-set-revs', pid, (sets.data ?? []).map((s) => s.id).join(',')],
    enabled: Boolean(sets.data?.length),
    queryFn: () => Promise.all((sets.data ?? []).map((s) => api<SetDetail>(`/psd/sets/${s.id}`))),
  });
  const [f, setF] = useState({
    date: todayLocal(),
    counterpartyId: '',
    recipientKind: (direction === 'in' ? 'designer' : 'contractor') as RecipientKind,
    externalNumber: '',
    note: '',
  });
  const [items, setItems] = useState<Record<string, { copies: number; paper: boolean }>>({});
  const options = (details.data ?? []).flatMap((s) =>
    s.revisions
      .filter((r) =>
        direction === 'out'
          ? ['review', 'approved', 'production'].includes(r.status)
          : r.status !== 'annulled',
      )
      .map((r) => ({ id: r.id, label: `${s.code} ${r.code}`, status: r.status })),
  );
  const m = useMutation({
    mutationFn: () =>
      api<TransmittalView>('/psd/transmittals', {
        body: {
          projectId: pid,
          direction,
          date: f.date,
          counterpartyId: f.counterpartyId || null,
          recipientKind: f.recipientKind,
          externalNumber: f.externalNumber.trim() || null,
          note: f.note.trim() || null,
          items: Object.entries(items).map(([revisionId, v]) => ({ revisionId, ...v })),
        },
      }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['psd-transmittals'] });
      qc.invalidateQueries({ queryKey: ['psd-sets'] });
      toast(`Накладная ${t.number} создана`);
      onClose();
      navigate({ to: '/psd/transmittals/$transmittalId', params: { transmittalId: t.id } });
    },
  });
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title={direction === 'in' ? 'Входящая накладная от проектировщика' : 'Исходящая накладная'}
      description={
        direction === 'in'
          ? 'Состав комплекта, число экземпляров и бумажные оригиналы. Получение комплектов отметится сразу.'
          : 'Ревизии фиксируются на момент отправки: реестр покажет, у кого какая ревизия на руках.'
      }
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!pid || !Object.keys(items).length}
            onClick={() => m.mutate()}
          >
            Создать накладную
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект" className="sm:col-span-2">
          <Select
            className="h-11 md:h-10"
            value={pid}
            onChange={(e) => {
              setPid(e.target.value);
              setItems({});
            }}
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Дата">
          <Input
            type="date"
            className="h-11 md:h-10"
            value={f.date}
            onChange={(e) => setF({ ...f, date: e.target.value })}
          />
        </Field>
        {direction === 'in' ? (
          <Field label="Номер накладной проектировщика">
            <Input
              className="h-11 md:h-10"
              value={f.externalNumber}
              onChange={(e) => setF({ ...f, externalNumber: e.target.value })}
            />
          </Field>
        ) : (
          <Field label="Кому">
            <Select
              className="h-11 md:h-10"
              value={f.recipientKind}
              onChange={(e) => setF({ ...f, recipientKind: e.target.value as RecipientKind })}
            >
              {(Object.keys(RECIPIENT_KINDS) as RecipientKind[])
                .filter((k) => k !== 'designer')
                .map((k) => (
                  <option key={k} value={k}>
                    {RECIPIENT_KINDS[k]}
                  </option>
                ))}
            </Select>
          </Field>
        )}
        <Field label={direction === 'in' ? 'Проектировщик' : 'Организация'} className="sm:col-span-2">
          <Select
            className="h-11 md:h-10"
            value={f.counterpartyId}
            onChange={(e) => setF({ ...f, counterpartyId: e.target.value })}
          >
            <option value="">— не указана —</option>
            {(cps.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.shortName || c.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <div className="mb-1.5 text-[13px] font-medium text-ink">Комплекты и ревизии</div>
          {details.isFetching ? <Spinner /> : null}
          {!options.length && !details.isFetching ? (
            <p className="text-[13px] text-ink-2">В проекте нет ревизий, которые можно передать.</p>
          ) : null}
          <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
            {options.map((o) => {
              const it = items[o.id];
              return (
                <li key={o.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                  <Checkbox
                    className="min-h-11 flex-1 items-center md:min-h-0"
                    checked={Boolean(it)}
                    onChange={(e) =>
                      setItems((x) => {
                        const n = { ...x };
                        if (e.target.checked) n[o.id] = { copies: 1, paper: direction === 'in' };
                        else delete n[o.id];
                        return n;
                      })
                    }
                    label={o.label}
                  />
                  {it ? (
                    <>
                      <span className="flex items-center gap-1.5 text-[13px] text-ink-2">
                        экз.
                        <Input
                          aria-label="Число экземпляров"
                          type="number"
                          min={0}
                          max={100}
                          value={it.copies}
                          onChange={(e) =>
                            setItems((x) => ({
                              ...x,
                              [o.id]: { ...it, copies: Math.max(0, Number(e.target.value) || 0) },
                            }))
                          }
                          className="h-11 w-20 md:h-9"
                        />
                      </span>
                      <Checkbox
                        checked={it.paper}
                        onChange={(e) =>
                          setItems((x) => ({ ...x, [o.id]: { ...it, paper: e.target.checked } }))
                        }
                        label="бумажный оригинал"
                      />
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
        <Field label="Примечание" className="sm:col-span-2">
          <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

export function TransmittalPage() {
  const { transmittalId } = useParams({ strict: false }) as { transmittalId: string };
  const qc = useQueryClient();
  const me = useMe();
  const q = useQuery({
    queryKey: ['psd-transmittal', transmittalId],
    queryFn: () => api<TransmittalView>(`/psd/transmittals/${transmittalId}`),
  });
  useCrumbs(
    q.data
      ? [
          { label: 'Накладные', to: `/psd?tab=transmittals&project=${q.data.projectId}` },
          { label: q.data.number },
        ]
      : null,
    q.error,
  );
  const act = useMutation({
    mutationFn: (a: 'send' | 'receive') =>
      api<TransmittalView>(`/psd/transmittals/${transmittalId}/${a}`, { method: 'POST' }),
    onSuccess: (t) => {
      qc.setQueryData(['psd-transmittal', transmittalId], t);
      qc.invalidateQueries({ queryKey: ['psd-transmittals'] });
      toast(t.status === 'sent' ? 'Накладная отправлена' : 'Получение отмечено');
    },
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const t = q.data;
  const canAct = canAnywhere(me.access, 'psd.upload');
  return (
    <Page>
      <PageHeader
        title={`Накладная ${t.number}`}
        subtitle={`${TRANSMITTAL_DIRECTIONS[t.direction]} · ${date(t.date)}`}
        actions={
          canAct && t.status === 'draft' ? (
            <Button variant="primary" size="lg" loading={act.isPending} onClick={() => act.mutate('send')}>
              Отправить
            </Button>
          ) : canAct && t.status === 'sent' ? (
            <Button variant="primary" size="lg" loading={act.isPending} onClick={() => act.mutate('receive')}>
              Отметить получение
            </Button>
          ) : null
        }
      >
        <div className="mt-2">
          <Badge tone={t.status === 'draft' ? 'neutral' : t.status === 'sent' ? 'info' : 'success'}>
            {TRANSMITTAL_STATUSES[t.status]}
          </Badge>
        </div>
      </PageHeader>
      <ErrorBox error={act.error} className="mb-4" />
      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        <Panel title="Состав" bodyClassName="p-0">
          <ul className="divide-y divide-line">
            {t.items.map((i) => (
              <li
                key={i.revisionId}
                className="flex flex-col gap-1 px-5 py-3 md:flex-row md:items-center md:gap-4"
              >
                <Link
                  to="/psd/sets/$setId"
                  params={{ setId: i.setId }}
                  className="flex min-h-11 min-w-0 flex-1 flex-col justify-center hover:underline md:min-h-0"
                >
                  <span className="font-medium [overflow-wrap:anywhere] text-ink">
                    {i.setCode} {i.revision}
                  </span>
                  <span className="text-[13px] text-ink-2">{i.setName}</span>
                </Link>
                <span className="text-[13px] text-ink-2">
                  {plural(i.sheetCount, 'лист', 'листа', 'листов')} · {i.copies} экз.
                  {i.paper ? ' · бумажный оригинал' : ''}
                </span>
                <RevisionBadge status={i.revisionStatus} />
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Реквизиты">
          <KeyValue
            items={[
              { label: 'Проект', value: t.projectCode },
              {
                label: t.direction === 'in' ? 'От кого' : 'Кому',
                value:
                  t.party ?? (t.recipientKind ? RECIPIENT_KINDS[t.recipientKind as RecipientKind] : null),
              },
              { label: 'Номер отправителя', value: t.externalNumber },
              { label: 'Составил', value: t.createdBy },
              {
                label: 'Получено',
                value: t.receivedAt
                  ? `${dateTime(t.receivedAt)}${t.receivedBy ? `, ${t.receivedBy}` : ''}`
                  : null,
              },
              { label: 'Примечание', value: t.note },
            ]}
          />
          {t.direction === 'out' ? (
            <p className="mt-3 text-[12px] text-ink-2">Подпись накладной ЭЦП — после подключения NCALayer.</p>
          ) : null}
        </Panel>
        {t.direction === 'out' && canAct ? (
          <div className="lg:col-start-2">
            <TransmittalLinks transmittalId={t.id} sent={t.status !== 'draft'} />
          </div>
        ) : null}
      </div>
    </Page>
  );
}
